import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Fab,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  Image as ImageIcon,
  InsertDriveFile as InsertDriveFileIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_BASE_URL } from '../config/api';
import { alpha } from '@mui/material/styles';
import {
  normalizeMojibakeFilename,
  buildSchemaDownloadUrl,
  buildSchemaStaticUrl,
  parseSchemaStoredName,
  toSchemaDownloadApiPath
} from '../utils/schemaFiles';

interface SchemaPanel {
  id: string;
  name: string;
  description: string;
  content: string;
  order: number;
  isActive: boolean;
  category?: string | null;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  updater?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface PanelFormData {
  name: string;
  description: string;
  content: string;
  category: string;
  order: number;
  isActive: boolean;
  image?: File;
}

const Schemos: React.FC = () => {
  const { user, hasPermission, token } = useAuth();
  const { t } = useTranslation();
  const [panels, setPanels] = useState<SchemaPanel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPanel, setEditingPanel] = useState<SchemaPanel | null>(null);
  const [formData, setFormData] = useState<PanelFormData>({
    name: '',
    description: '',
    content: '',
    category: '',
    order: 0,
    isActive: true
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fullSizeImage, setFullSizeImage] = useState<{ url: string; alt: string } | null>(null);
  const [fullSizeImageLoading, setFullSizeImageLoading] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const isAdmin = hasPermission('schemos.edit') || hasPermission('schemos.delete');

  const { uncategorizedPanels, categoryGroups } = useMemo(() => {
    const uncategorized: SchemaPanel[] = [];
    const byCategory = new Map<string, SchemaPanel[]>();
    for (const p of panels) {
      const raw = p.category != null ? String(p.category).trim() : '';
      if (!raw) uncategorized.push(p);
      else {
        const list = byCategory.get(raw) ?? [];
        list.push(p);
        byCategory.set(raw, list);
      }
    }
    const categoryGroups = Array.from(byCategory.keys())
      .sort((a, b) => a.localeCompare(b, 'lt'))
      .map((key) => ({ key, panels: byCategory.get(key)! }));
    return { uncategorizedPanels: uncategorized, categoryGroups };
  }, [panels]);

  const fetchPanels = async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin ? '/schemas/admin' : '/schemas';
      const response = await fetch(getApiUrl(endpoint), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch panels');
      }

      const data = await response.json();
      setPanels(data.panels);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch panels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPanels();
  }, [isAdmin]);

  const handlePanelChange = (panelId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panelId : false);
  };

  const handleCategoryAccordionChange =
    (categoryKey: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCategory(isExpanded ? categoryKey : false);
    };

  const handleOpenDialog = (panel?: SchemaPanel) => {
    if (panel) {
      setEditingPanel(panel);
      setFormData({
        name: panel.name,
        description: panel.description,
        content: panel.content,
        category: panel.category ?? '',
        order: panel.order,
        isActive: panel.isActive
      });
      setImagePreview(panel.imageUrl ? `${API_BASE_URL}${panel.imageUrl}` : null);
    } else {
      setEditingPanel(null);
      setFormData({
        name: '',
        description: '',
        content: '',
        category: '',
        order: 0,
        isActive: true
      });
      setImagePreview(null);
    }
    setSelectedImage(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPanel(null);
    setFormData({
      name: '',
      description: '',
      content: '',
      category: '',
      order: 0,
      isActive: true
    });
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleFormChange = (field: keyof PanelFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'order' ? parseInt(value as string) || 0 : value
    }));
  };

  const safeAttachmentLabel = (name: string) => {
    const s = normalizeMojibakeFilename(name).replace(/[\[\]]/g, '').trim();
    return s || 'file';
  };

  const handleSchemaFileDownload = async (downloadUrl: string, filename: string) => {
    const apiPath = toSchemaDownloadApiPath(downloadUrl, API_BASE_URL);
    const authToken = token || localStorage.getItem('token');
    try {
      const response = await fetch(getApiUrl(apiPath), {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = safeAttachmentLabel(filename);
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);
      document.body.removeChild(anchor);
    } catch (err) {
      console.error('Error downloading schema file:', err);
      setError(t('schemos.downloadError', { defaultValue: 'Failed to download file' }));
    }
  };

  // Paste handler for images: uploads to /api/schemas/upload and inserts markdown image reference
  const handleContentPaste = useCallback(async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        try {
          const uploadForm = new FormData();
          uploadForm.append('image', file);
          uploadForm.append('originalFilename', file.name);
          const response = await fetch(getApiUrl('/schemas/upload'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: uploadForm
          });
          if (!response.ok) {
            throw new Error('Upload failed');
          }
          const data = await response.json();
          const originalName = file.name || data.originalName || 'image';
          const storedUrl = data.imageUrl || data.fileUrl;
          const downloadUrl = buildSchemaDownloadUrl(storedUrl, originalName, API_BASE_URL);
          setFormData(prev => ({
            ...prev,
            content: `${prev.content}${prev.content ? '\n' : ''}![${safeAttachmentLabel(originalName)}](${downloadUrl})\n`
          }));
        } catch (err) {
          setError('Failed to paste image');
        }
      }
    }
  }, [setFormData, token]);

  const handleAttachContentFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadingAttachment(true);
    setError(null);
    try {
      const uploadForm = new FormData();
      uploadForm.append('file', file);
      uploadForm.append('originalFilename', file.name);
      const response = await fetch(getApiUrl('/schemas/upload'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: uploadForm
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const msg =
          typeof errBody.details === 'string'
            ? errBody.details
            : Array.isArray(errBody.details)
            ? errBody.details.map((d: { msg?: string }) => d.msg).filter(Boolean).join(', ')
            : errBody.error || 'Upload failed (max 150MB)';
        throw new Error(msg);
      }
      const data = await response.json();
      const originalName = file.name || data.originalName;
      const storedUrl = data.fileUrl || data.imageUrl;
      const url = buildSchemaDownloadUrl(storedUrl, originalName, API_BASE_URL);
      const isImage =
        (data.mimeType && String(data.mimeType).startsWith('image/')) ||
        /\.(jpe?g|png|gif|webp)$/i.test(file.name);
      const line = isImage
        ? `![${safeAttachmentLabel(originalName)}](${url})\n`
        : `[${safeAttachmentLabel(originalName)}](${url})\n`;
      setFormData((prev) => ({
        ...prev,
        content: `${prev.content}${prev.content ? '\n' : ''}${line}`
      }));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('schemos.attachUploadError', { defaultValue: 'Failed to upload file' })
      );
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = (imageUrl: string, altText: string) => {
    const fullUrl = `${API_BASE_URL}${imageUrl}`;
    setFullSizeImageLoading(true);
    setFullSizeImage({ url: fullUrl, alt: altText });
  };

  const linkIconForUrl = (url: string) => {
    const ext = (url.split('.').pop() || '').toLowerCase().split('?')[0];
    const sx = { fontSize: 18, verticalAlign: 'text-bottom', mr: 0.25, opacity: 0.9 };
    if (ext === 'pdf') return <PictureAsPdfIcon sx={sx} color="error" />;
    if (ext === 'doc' || ext === 'docx') return <DescriptionIcon sx={sx} color="primary" />;
    if (ext === 'xls' || ext === 'xlsx') return <TableChartIcon sx={sx} color="success" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <ImageIcon sx={sx} color="action" />;
    return <InsertDriveFileIcon sx={sx} color="action" />;
  };

  /** Renders plain text newlines plus markdown links [label](url) — used between image tokens */
  const renderTextWithLinks = (text: string, segmentKey: string) => {
    const nodes: React.ReactNode[] = [];
    const linkRegex = /\[([^\]]*)\]\(([^\s)]+)\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyIndex = 0;

    const pushPlain = (chunk: string) => {
      if (!chunk) return;
      const parts = chunk.split('\n');
      parts.forEach((part, idx) => {
        if (part) nodes.push(<span key={`${segmentKey}-t-${keyIndex++}`}>{part}</span>);
        if (idx < parts.length - 1) nodes.push(<br key={`${segmentKey}-br-${keyIndex++}`} />);
      });
    };

    while ((match = linkRegex.exec(text)) !== null) {
      const [full, label, url] = match;
      const start = match.index;
      pushPlain(text.slice(lastIndex, start));
      const displayLabel = safeAttachmentLabel(label || url);
      const downloadUrl = buildSchemaDownloadUrl(url, displayLabel, API_BASE_URL);
      nodes.push(
        <Box
          key={`${segmentKey}-link-${start}`}
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap', verticalAlign: 'middle' }}
        >
          {linkIconForUrl(url)}
          <Link
            component="button"
            type="button"
            underline="hover"
            sx={{ cursor: 'pointer', font: 'inherit', verticalAlign: 'baseline' }}
            onClick={() => handleSchemaFileDownload(downloadUrl, displayLabel)}
          >
            {displayLabel}
          </Link>
        </Box>
      );
      lastIndex = start + full.length;
    }
    pushPlain(text.slice(lastIndex));
    return nodes;
  };

  const renderContentWithImages = (content: string) => {
    const nodes: React.ReactNode[] = [];
    const imageRegex = /!\[(.*?)\]\(([^\s)]+)\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyIndex = 0;

    while ((match = imageRegex.exec(content)) !== null) {
      const [, alt, url] = match;
      const start = match.index;
      const end = start + match[0].length;
      nodes.push(...renderTextWithLinks(content.slice(lastIndex, start), `pre-${lastIndex}`));
      const displayAlt = safeAttachmentLabel(alt || 'Image');
      const storedName = parseSchemaStoredName(url, API_BASE_URL);
      const staticPath = storedName ? buildSchemaStaticUrl(storedName) : buildSchemaDownloadUrl(url, displayAlt, API_BASE_URL);
      const src = `${API_BASE_URL}${staticPath.startsWith('/') ? staticPath : `/${staticPath}`}`;
      nodes.push(
        <img
          key={`img-${start}-${keyIndex++}`}
          src={src}
          alt={displayAlt}
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            objectFit: 'contain',
            borderRadius: '8px',
            display: 'block',
            marginTop: '8px',
            marginBottom: '8px',
            cursor: 'pointer'
          }}
          onClick={() => handleImageClick(staticPath, displayAlt)}
        />
      );
      lastIndex = end;
    }
    nodes.push(...renderTextWithLinks(content.slice(lastIndex), `tail-${lastIndex}`));
    return nodes;
  };

  const handleCloseFullSizeImage = () => {
    setFullSizeImage(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && fullSizeImage) {
        handleCloseFullSizeImage();
      }
    };

    if (fullSizeImage) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullSizeImage]);

  const handleSubmit = async () => {
    try {
      const endpoint = editingPanel 
        ? `/schemas/${editingPanel.id}`
        : '/schemas';
      const method = editingPanel ? 'PUT' : 'POST';

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('category', formData.category.trim());
      formDataToSend.append('order', formData.order.toString());
      formDataToSend.append('isActive', formData.isActive.toString());
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      const response = await fetch(getApiUrl(endpoint), {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Schema creation failed:', errorData);
        if (errorData.details) {
          const errorMessages = errorData.details.map((err: any) => `${err.param}: ${err.msg}`).join(', ');
          throw new Error(`Validation failed: ${errorMessages}`);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error('Failed to save panel');
        }
      }

      handleCloseDialog();
      fetchPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save panel');
    }
  };

  const handleDelete = async (panelId: string) => {
    if (!window.confirm('Are you sure you want to delete this panel?')) {
      return;
    }
    try {
      const response = await fetch(getApiUrl(`/schemas/${panelId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to delete panel');
      }
      fetchPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete panel');
    }
  };

  const handleToggleActive = async (panel: SchemaPanel) => {
    try {
      const response = await fetch(getApiUrl(`/schemas/${panel.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...panel,
          isActive: !panel.isActive
        })
      });
      if (!response.ok) {
        throw new Error('Failed to update panel');
      }
      fetchPanels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update panel');
    }
  };

  const renderPanelAccordion = (panel: SchemaPanel) => (
    <Accordion
      key={panel.id}
      expanded={expandedPanel === panel.id}
      onChange={handlePanelChange(panel.id)}
      sx={{ mb: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">{panel.name}</Typography>
            <Typography variant="body2" color="text.secondary">{panel.description}</Typography>
          </Box>
        </Box>
      </AccordionSummary>

      {isAdmin && (
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <Chip label={panel.isActive ? t('schemos.active') : t('schemos.inactive')} color={panel.isActive ? 'success' : 'default'} size="small" />
          <IconButton size="small" onClick={() => handleToggleActive(panel)}>
            {panel.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
          <IconButton size="small" onClick={() => handleOpenDialog(panel)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(panel.id)} color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      )}

      <AccordionDetails>
        <Box sx={{ whiteSpace: 'pre-wrap' }}>
          {renderContentWithImages(panel.content)}
        </Box>
        {panel.imageUrl && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Tooltip title="Click to view full size" placement="top">
              <img
                src={`${API_BASE_URL}${panel.imageUrl}`}
                alt={panel.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                onClick={() => handleImageClick(panel.imageUrl!, panel.name)}
              />
            </Tooltip>
          </Box>
        )}
        {isAdmin && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Created by: {panel.creator.firstName} {panel.creator.lastName} on {new Date(panel.createdAt).toLocaleDateString()}
            </Typography>
            {panel.updater && (
              <Typography variant="caption" color="text.secondary" display="block">
                Updated by: {panel.updater.firstName} {panel.updater.lastName} on {new Date(panel.updatedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('schemos.title', 'Schemos')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {panels.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" align="center">
              {t('schemos.noPanels', 'No procedure panels available')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {uncategorizedPanels.map((panel) => renderPanelAccordion(panel))}
          {categoryGroups.map(({ key, panels: grouped }) => (
            <Accordion
              key={key}
              expanded={expandedCategory === key}
              onChange={handleCategoryAccordionChange(key)}
              elevation={0}
              sx={(theme) => ({
                mb: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.28),
                overflow: 'hidden',
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.03),
                '&:before': { display: 'none' }
              })}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={(theme) => ({
                  minHeight: 52,
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.09),
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.22),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.12)
                  },
                  '& .MuiAccordionSummary-content': { my: 1.25 }
                })}
              >
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                  {key}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 0, pb: 1 }}>
                <Box sx={{ pl: { xs: 0, sm: 1 } }}>
                  {grouped.map((panel) => renderPanelAccordion(panel))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {isAdmin && (
        <Tooltip title={t('schemos.addNewPanel')}>
          <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: 16, right: 16 }} onClick={() => handleOpenDialog()}>
            <AddIcon />
          </Fab>
        </Tooltip>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingPanel ? t('schemos.editPanel') : t('schemos.addNewPanel')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth label={t('schemos.panelName')} value={formData.name} onChange={handleFormChange('name')} required />
            <TextField fullWidth label={t('schemos.panelDescription')} value={formData.description} onChange={handleFormChange('description')} multiline rows={2} required />
            <TextField
              fullWidth
              label={t('schemos.panelCategory', 'Kategorija')}
              value={formData.category}
              onChange={handleFormChange('category')}
              helperText={t('schemos.panelCategoryHint', {
                defaultValue: 'Leave empty for top-level panels. Same label groups panels under one accordion.'
              })}
            />
            <TextField fullWidth label={t('schemos.panelContent')} value={formData.content} onChange={handleFormChange('content')} onPaste={handleContentPaste} multiline rows={6} required />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('schemos.attachFileSection', 'Attachments in content')}</Typography>
              <input
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                id="schema-content-file-upload"
                type="file"
                onChange={handleAttachContentFile}
              />
              <label htmlFor="schema-content-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploadingAttachment ? <CircularProgress size={18} /> : <AttachFileIcon />}
                  disabled={uploadingAttachment}
                  fullWidth
                >
                  {t('schemos.attachFile', 'Attach file')}
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'schemos.attachFileHint',
                  'PDF, Word, Excel, or images (max 20 MB). Inserts a link or image into the content field.'
                )}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('schemos.panelImage')}</Typography>
              <input accept="image/*" style={{ display: 'none' }} id="image-upload-schema" type="file" onChange={handleImageChange} />
              <label htmlFor="image-upload-schema">
                <Button variant="outlined" component="span" startIcon={<AddIcon />} fullWidth>
                  {imagePreview ? t('schemos.changeImage') : t('schemos.uploadImage')}
                </Button>
              </label>
              {imagePreview && (
                <Box sx={{ mt: 1 }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }} />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label={t('schemos.order')} type="number" value={formData.order} onChange={handleFormChange('order')} inputProps={{ min: 0 }} />
              <FormControlLabel control={<Switch checked={formData.isActive} onChange={handleFormChange('isActive')} />} label={t('schemos.panelActive')} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('schemos.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">{editingPanel ? t('schemos.update') : t('schemos.create')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!fullSizeImage} onClose={handleCloseFullSizeImage} maxWidth={false} fullWidth
        PaperProps={{ sx: { maxWidth: '90vw', maxHeight: '90vh', backgroundColor: 'transparent', boxShadow: 'none' } }}>
        <DialogContent sx={{ p: 0, textAlign: 'center', backgroundColor: 'transparent' }}>
          {fullSizeImage && (
            <Box sx={{ position: 'relative' }}>
              {fullSizeImageLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                  <CircularProgress />
                </Box>
              )}
              <img
                src={fullSizeImage.url}
                alt={fullSizeImage.alt}
                style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px', display: fullSizeImageLoading ? 'none' : 'block' }}
                onLoad={() => setFullSizeImageLoading(false)}
                onError={() => setFullSizeImageLoading(false)}
              />
              <IconButton onClick={handleCloseFullSizeImage} sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Schemos;


